import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import api from "../api/axios";
import { ORDER_STAGES } from "../utils/orderTrackingStages";
import socket from "../socket";
import ReviewDialog from "../components/reviews/ReviewDialog";

const OrderDetails = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [openReview, setOpenReview] = useState(false);
  const [reviewCtx, setReviewCtx] = useState(null);

  useEffect(() => {
    socket.emit("join_order", orderId);
    socket.on("order_status_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
        }));
      }
    });
    return () => socket.off("order_status_updated");
  }, [orderId]);

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((res) => setOrder(res.data.order))
      .catch(() => setOrder(null));
  }, [orderId]);

  const openReviewDialog = async (productId) => {
    const res = await api.get(
      `/reviews/eligibility/${productId}/${order._id}`
    );

    setReviewCtx({
      productId,
      orderId: order._id,
      mode: res.data.mode,
      reviewId: res.data.reviewId,
    });

    setOpenReview(true);
  };

  if (!order) return <Typography>Order not found</Typography>;

  const downloadInvoice = () => {
    const pdf = new jsPDF();
    pdf.text("INVOICE", 20, 20);
    pdf.text(`Order ID: ${order._id}`, 20, 30);
    pdf.text(
      `Date: ${new Date(order.createdAt).toLocaleDateString()}`,
      20,
      40
    );
    pdf.text(`Payment: ${order.paymentMethod}`, 20, 50);
    pdf.save(`Invoice_${order._id}.pdf`);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
      {/* Order Summary */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} mb={1}>
          Order Details
        </Typography>

        <Typography fontSize={14}>
          <b>Order ID:</b> {order._id}
        </Typography>
        <Typography fontSize={14}>
          <b>Order Date:</b>{" "}
          {new Date(order.createdAt).toLocaleDateString()}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {order.items.map((item, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography fontWeight={600}>
              {item.title}
            </Typography>
            <Typography fontSize={14}>
              Qty: {item.qty} · ₹
              {Number(item.price * item.qty).toLocaleString("en-IN")}
            </Typography>

            {order.status === "DELIVERED" && (
              <Button
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#007185",
                  fontWeight: 600,
                  mt: 0.5,
                }}
                onClick={() =>
                  openReviewDialog(item.productId)
                }
              >
                Write / Edit Review
              </Button>
            )}
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography fontWeight={700}>
          Order Total: ₹
          {Number(order.total).toLocaleString("en-IN")}
        </Typography>

        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={downloadInvoice}
        >
          Download Invoice
        </Button>
      </Paper>

      {/* Tracking */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography fontWeight={700} mb={2}>
          Order Tracking
        </Typography>

        {ORDER_STAGES.map((stage,index) => {
          const completed = order.tracking.some(
            (t) => t.key === stage.key
          );
              const trackingEntry = order.tracking.find(
                    (t) => t.key === stage.key
                  );

          return (
            <Stack
              key={stage.key}
              direction="row"
              spacing={2}
              mb={1}
              alignItems="center"
              sx={{ opacity: completed ? 1 : 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: completed
                    ? "#2e7d32"
                    : "#bdbdbd",
                }}
              />
              <Typography>{stage.label}</Typography>
                        {trackingEntry?.time && (
            <Typography variant="caption" color="text.secondary">
              {new Date(trackingEntry.time).toLocaleString()}
            </Typography>
          )}
            </Stack>
          );
        })}
      </Paper>

      {reviewCtx && (
        <ReviewDialog
          open={openReview}
          onClose={() => setOpenReview(false)}
          productId={reviewCtx.productId}
          orderId={reviewCtx.orderId}
          mode={reviewCtx.mode}
          reviewId={reviewCtx.reviewId}
        />
      )}
    </Box>
  );
};

export default OrderDetails;
